import * as React from 'react'

import {
  CheckCircle2,
  ChevronDown,
  Download,
  Pencil,
  Send,
  Upload,
  UserPlus,
} from 'lucide-react'

import ListSelectionToolbar from '~/client/src/desktop/tables/ListSelectionToolbar'
import type { UseTableListResult } from '~/client/src/desktop/tables/useTableList'
import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IUserRow } from '../types'

interface ISelectionToolbarProps {
  list: UseTableListResult<IUserRow>
  hasInvitableSelected: boolean
  hasApprovableSelected: boolean
  onEdit: () => void
  onInvite: () => void
  onApprove: () => void
  onAddUser: () => void
  onExport: () => void
  onUpload: (file: File) => void
  onConnectProcore: () => void
  isExporting: boolean
  isUploading: boolean
  isConnectingProcore: boolean
}

export default function SelectionToolbar({
  list,
  hasInvitableSelected,
  hasApprovableSelected,
  onEdit,
  onInvite,
  onApprove,
  onAddUser,
  onExport,
  onUpload,
  onConnectProcore,
  isExporting,
  isUploading,
  isConnectingProcore,
}: ISelectionToolbarProps): React.ReactElement {
  const filePicker = React.useRef<IFilePickerActions>(null)

  return (
    <ListSelectionToolbar
      list={list}
      countLabel={Localization.translator.xUsersCount(list.model.filteredCount)}
      selectionActions={
        <>
          <Button variant="primary-ghost" onClick={onEdit}>
            <Pencil aria-hidden="true" />
            {Localization.translator.edit_verb}
          </Button>
          <Button
            variant="primary-ghost"
            onClick={onInvite}
            disabled={!hasInvitableSelected}
          >
            <Send aria-hidden="true" />
            {Localization.translator.invite}
          </Button>
          <Button
            variant="primary-ghost"
            onClick={onApprove}
            disabled={!hasApprovableSelected}
          >
            <CheckCircle2 aria-hidden="true" />
            {Localization.translator.approve_verb}
          </Button>
        </>
      }
    >
      <FilePicker
        actionsRef={filePicker}
        accept=".csv"
        onPick={([file]) => onUpload(file)}
      />

      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size="lg">
              <span>{Localization.translator.importExportTeamMembers}</span>
              <ChevronDown aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onExport} disabled={isExporting}>
            <Download aria-hidden="true" />
            {
              Localization.translator.teamMembersDropdownStates
                .downloadTeamMembers
            }
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => filePicker.current?.open()}
            disabled={isUploading}
          >
            <Upload aria-hidden="true" />
            {
              Localization.translator.teamMembersDropdownStates
                .uploadTeamMembers
            }
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onConnectProcore}
            disabled={isConnectingProcore}
          >
            <img
              src="/static/icons/procore-icon.svg"
              alt=""
              aria-hidden="true"
              className="tw:size-4"
            />
            {Localization.translator.teamMembersDropdownStates.connectToProcore}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="lg" onClick={onAddUser}>
        <UserPlus aria-hidden="true" />
        {Localization.translator.addNewUser}
      </Button>
    </ListSelectionToolbar>
  )
}
